import { useEffect, useState, useMemo } from "react";
import { X, ChartLineUp, CaretLeft, CaretRight, Printer, Warning, CurrencyEur, CaretDown, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { openPrintWindow } from "../lib/printWindow";
import { SortHeader } from "./SortHeader";
import { useTableSort } from "../lib/useTableSort";

const PERIODS = [
  { id: "day", label: "Día" },
  { id: "week", label: "Semana" },
  { id: "month", label: "Mes" },
];

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};
const fmtDateOnly = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
  catch { return iso; }
};
const fmtEUR = (n) => `${Number(n || 0).toFixed(2)} €`;

export default function WorkerHistoryDialog({ worker, onClose, onOpenIncidence }) {
  const [period, setPeriod] = useState("week");
  const [refDate, setRefDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const histSort = useTableSort(data?.punches || [], "start_time", "desc");
  const [loading, setLoading] = useState(true);
  const [myIncidences, setMyIncidences] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [advancesOpen, setAdvancesOpen] = useState(false);
  const pin = worker.__pin || "";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .post(`/workers/${worker.id}/self-report`, { pin, period, ref_date: refDate })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) toast.error(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [worker.id, pin, period, refDate]);

  // Fetch my incidences once — the same PIN was already validated to open this dialog
  useEffect(() => {
    let alive = true;
    api
      .post("/incidences/mine", { user_id: worker.id, pin })
      .then((r) => { if (alive) setMyIncidences(r.data || []); })
      .catch(() => { if (alive) setMyIncidences([]); });
    return () => { alive = false; };
  }, [worker.id, pin]);

  // Fetch my advances once
  useEffect(() => {
    let alive = true;
    api
      .post(`/workers/${worker.id}/advances/public`, { pin })
      .then((r) => { if (alive) setAdvances(r.data || []); })
      .catch(() => { if (alive) setAdvances([]); });
    return () => { alive = false; };
  }, [worker.id, pin]);

  const advancesSummary = useMemo(() => {
    return advances.reduce(
      (acc, a) => {
        acc.total += a.amount || 0;
        acc.paid += a.total_paid || 0;
        acc.pending += a.pending || 0;
        acc.pending_discount += a.discount_enabled ? (a.pending || 0) : 0;
        if (a.settled) acc.settled += 1;
        return acc;
      },
      { total: 0, paid: 0, pending: 0, pending_discount: 0, settled: 0 }
    );
  }, [advances]);

  const shiftPeriod = (delta) => {
    const d = new Date(refDate + "T00:00:00");
    if (period === "day") d.setDate(d.getDate() + delta);
    else if (period === "week") d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setRefDate(d.toISOString().slice(0, 10));
  };

  const rangeLabel = useMemo(() => {
    if (!data) return "";
    const s = new Date(data.range_start);
    const e = new Date(data.range_end);
    if (period === "day") return s.toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    if (period === "week") return `${fmtDate(s)} — ${fmtDate(new Date(e.getTime() - 86400000))}`;
    return s.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  }, [data, period]);

  const escapeHtml = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const printList = () => {
    if (!data || !(data.punches?.length)) { toast.error("No hay fichajes que imprimir"); return; }
    // Merge punches + discounting breaks (same rules as timeline) sorted descending by time.
    const items = [];
    for (const p of data.punches || []) {
      items.push({ kind: "punch", ts: new Date(p.start_time).getTime(), data: p });
    }
    for (const b of data.breaks || []) {
      if (!b.discounts) continue;
      items.push({ kind: "break", ts: new Date(b.start_time).getTime(), data: b });
    }
    items.sort((a, b) => b.ts - a.ts);
    const rows = items.map((it) => {
      if (it.kind === "break") {
        const b = it.data;
        const color = b.family_color || "#f59e0b";
        return `<tr class="brk" style="background:${color}14;border-left:3px solid ${color}">
          <td>${escapeHtml(fmt(b.start_time))}<div class="brk-tag" style="background:${color}">TAREA · ${escapeHtml(b.family_name || "")}${b.family_name ? " · " : ""}${escapeHtml(b.reason || "")}${b.custom_reason ? " — " + escapeHtml(b.custom_reason) : ""}</div></td>
          <td>${b.is_open ? "EN CURSO" : escapeHtml(fmt(b.end_time))}</td>
          <td style="text-align:right">-${b.duration_hours.toFixed(2)} h</td>
          <td style="text-align:center">Descuenta</td>
        </tr>`;
      }
      const p = it.data;
      const skipped = p.auto_closed || p.excluded;
      const estado = p.auto_closed ? "Auto-cierre" : p.excluded ? "Excluido" : p.origin === "incidence" ? "Incidencia" : "Normal";
      return `<tr class="${skipped ? "skip" : ""}">
        <td>${escapeHtml(fmt(p.start_time))}</td>
        <td>${p.is_open ? "EN CURSO" : escapeHtml(fmt(p.end_time))}</td>
        <td style="text-align:right">${p.duration_hours.toFixed(2)} h</td>
        <td style="text-align:center">${escapeHtml(estado)}</td>
      </tr>`;
    }).join("");
    const discountingBreakHours = (data.breaks || []).filter((b) => b.discounts).reduce((s, b) => s + (b.duration_hours || 0), 0);
    const rawHours = data.worked_hours_raw;
    const discountNote = (data.discounted_hours ?? discountingBreakHours) > 0
      ? `El total ya tiene descontadas ${(data.discounted_hours ?? discountingBreakHours).toFixed(2)} h de tareas que descuentan${rawHours && Math.abs(rawHours - (data.worked_hours ?? 0)) > 0.01 ? ` (bruto: ${rawHours.toFixed(2)} h)` : ""}.`
      : "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Mis horas — ${escapeHtml(worker.name)}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:24px;color:#1a1a1a}
  h1{font-size:22px;margin:0 0 4px}
  .sub{font-size:12px;color:#666;text-transform:uppercase;letter-spacing:.08em;margin-bottom:16px}
  .total{background:#f5f5f0;padding:14px 16px;border-radius:10px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:center}
  .total b{font-size:26px;color:#5a7a4a}
  .brk-note{font-size:11px;color:#a16207;margin-top:-10px;margin-bottom:16px}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th{text-align:left;padding:8px;border-bottom:2px solid #ddd;text-transform:uppercase;font-size:10px;letter-spacing:.08em;color:#888}
  td{padding:8px;border-bottom:1px solid #eee;font-variant-numeric:tabular-nums;vertical-align:top}
  tr.skip td{color:#aaa;text-decoration:line-through}
  tr.brk td{font-size:12px}
  .brk-tag{display:inline-block;margin-top:4px;padding:2px 6px;border-radius:4px;color:#fff;font-size:9px;letter-spacing:.05em;text-transform:uppercase;font-weight:600}
  .foot{margin-top:20px;font-size:11px;color:#888;text-align:center}
  @media print { @page { margin: 12mm } }
</style></head><body>
  <h1>${escapeHtml(worker.name)}</h1>
  <div class="sub">Mis horas trabajadas · ${escapeHtml(rangeLabel)}</div>
  <div class="total"><span>Total trabajado</span><b>${(data.worked_hours ?? 0).toFixed(2)} h</b></div>
  ${discountNote ? `<div class="brk-note">${discountNote}</div>` : ""}
  <table>
    <thead><tr><th>Inicio</th><th>Fin</th><th style="text-align:right">Duración</th><th style="text-align:center">Estado</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="foot">Vista de solo lectura · Impreso ${new Date().toLocaleString("es-ES")}</div>
</body></html>`;
    const win = openPrintWindow(html);
    if (!win) toast.error("El navegador bloquea la ventana. Permite pop-ups y reintenta.");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="worker-history-dialog">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <ChartLineUp size={22} weight="duotone" className="text-[var(--primary)]" />
            <div>
              <div className="label-cap">Mis horas trabajadas</div>
              <h3 className="font-display font-bold text-xl">{worker.name}</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[var(--bg)]" data-testid="history-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 pt-4 pb-2 flex flex-wrap items-center gap-2 justify-between">
          <div className="inline-flex bg-[var(--bg)] rounded-xl p-1" role="tablist">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`history-period-${p.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-display font-semibold transition ${period === p.id ? "bg-white shadow-sm text-[var(--primary)]" : "text-[var(--text-secondary)] hover:text-[var(--foreground)]"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => shiftPeriod(-1)} data-testid="history-prev" className="w-9 h-9 rounded-lg border border-[var(--border)] flex items-center justify-center hover:bg-[var(--bg)]"><CaretLeft size={16} /></button>
            {period === "month" ? (
              <input
                type="month"
                value={refDate.slice(0, 7)}
                onChange={(e) => setRefDate(e.target.value + "-01")}
                data-testid="history-month-input"
                className="px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm"
              />
            ) : (
              <input
                type="date"
                value={refDate}
                onChange={(e) => setRefDate(e.target.value)}
                data-testid="history-date-input"
                className="px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm"
              />
            )}
            <button onClick={() => shiftPeriod(+1)} data-testid="history-next" className="w-9 h-9 rounded-lg border border-[var(--border)] flex items-center justify-center hover:bg-[var(--bg)]"><CaretRight size={16} /></button>
          </div>
        </div>

        <div className="px-5 pb-2 flex flex-wrap gap-2">
          <button
            onClick={() => { setPeriod("week"); setRefDate(new Date().toISOString().slice(0, 10)); }}
            data-testid="history-quick-week"
            className="px-3 py-1.5 rounded-full border border-[var(--border)] bg-white text-xs font-display font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg)] hover:text-[var(--primary)] transition"
          >
            Esta semana
          </button>
          <button
            onClick={() => {
              const d = new Date();
              setPeriod("month");
              setRefDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
            }}
            data-testid="history-quick-month"
            className="px-3 py-1.5 rounded-full border border-[var(--border)] bg-white text-xs font-display font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg)] hover:text-[var(--primary)] transition"
          >
            Este mes
          </button>
          <button
            onClick={() => {
              const d = new Date();
              d.setDate(1);
              d.setMonth(d.getMonth() - 1);
              setPeriod("month");
              setRefDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
            }}
            data-testid="history-quick-prev-month"
            className="px-3 py-1.5 rounded-full border border-[var(--border)] bg-white text-xs font-display font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg)] hover:text-[var(--primary)] transition"
          >
            Mes anterior
          </button>
        </div>

        <div className="px-5 pb-2 text-xs text-[var(--text-secondary)] capitalize" data-testid="history-range-label">{rangeLabel}</div>

        <div className="px-5 py-4 border-t border-b border-[var(--border)] bg-[var(--bg)]">
          <div className="text-xs uppercase tracking-widest text-[var(--text-secondary)] mb-1">Total trabajado</div>
          <div className="font-display font-bold text-3xl text-[var(--primary)]" data-testid="history-total">
            {loading ? "…" : (data?.worked_hours ?? 0).toFixed(2)} h
          </div>
          {!loading && data?.breaks && data.breaks.length > 0 && (
            <div className="mt-2 text-xs text-amber-700" data-testid="history-break-total">
              Total tareas: <span className="font-mono font-bold">{(data.break_hours ?? 0).toFixed(2)} h</span> ({data.breaks.length} tarea{data.breaks.length === 1 ? "" : "s"})
            </div>
          )}
        </div>

        {advances.length > 0 && (
          <div className="px-5 py-3 border-b border-[var(--border)] bg-emerald-50/60" data-testid="history-advances">
            <button
              onClick={() => setAdvancesOpen((v) => !v)}
              data-testid="history-advances-toggle"
              className="w-full flex items-center justify-between gap-3 text-left"
            >
              <div className="flex items-center gap-2">
                <CurrencyEur size={18} weight="duotone" className="text-emerald-700" />
                <div>
                  <div className="label-cap text-emerald-800">mis adelantos</div>
                  <div className="text-xs text-[var(--text-secondary)]">
                    {advances.length} adelanto{advances.length === 1 ? "" : "s"} · {advancesSummary.settled} saldado{advancesSummary.settled === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">Total</div>
                  <div className="font-mono font-bold text-sm">{fmtEUR(advancesSummary.total)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">Pendiente</div>
                  <div className="font-mono font-bold text-sm text-amber-700" data-testid="history-advances-pending">{fmtEUR(advancesSummary.pending)}</div>
                </div>
                <CaretDown size={16} weight="bold" className={`transition-transform ${advancesOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {advancesOpen && (
              <div className="mt-3 space-y-2" data-testid="history-advances-list">
                {advances.map((a) => (
                  <div key={a.id} data-testid={`history-advance-${a.id}`} className="bg-white rounded-lg border border-[var(--border)] px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-sm">{a.reason || "(sin motivo)"}</span>
                      <span className="text-xs text-[var(--text-secondary)]">· {fmtDateOnly(a.date)}</span>
                      {a.settled ? (
                        <span className="inline-flex items-center gap-1 ml-auto px-2 py-0.5 rounded-full text-[10px] font-display font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                          <CheckCircle size={12} weight="fill" /> Saldado
                        </span>
                      ) : (
                        <span className="ml-auto font-mono text-xs font-bold text-amber-700">
                          Pendiente {fmtEUR(a.pending)} / {fmtEUR(a.amount)}
                        </span>
                      )}
                    </div>
                    {a.payments.length > 0 && (
                      <ul className="mt-1.5 pl-3 text-xs text-[var(--text-secondary)] space-y-0.5">
                        {a.payments.map((p) => (
                          <li key={p.id} className="font-mono">
                            <span className="text-emerald-700">{fmtEUR(p.amount)}</span> · {fmtDateOnly(p.date)}
                            {p.notes && <span className="italic"> · {p.notes}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {myIncidences.length > 0 && (
            <div className="px-5 py-3 border-b border-[var(--border)] bg-[var(--bg)]" data-testid="history-my-incidences">
              <div className="label-cap mb-2">mis incidencias recientes</div>
              <ul className="space-y-2">
                {myIncidences.slice(0, 5).map((inc) => {
                  const isTarea = (inc.kind || "fichaje") === "tarea";
                  const parts = [];
                  if (inc.change_entry && inc.entry_time) parts.push(`${isTarea ? "Inicio" : "Entrada"} → ${inc.entry_time}`);
                  if (inc.change_exit && inc.exit_time) parts.push(`${isTarea ? "Fin" : "Salida"} → ${inc.exit_time}`);
                  const badge =
                    inc.status === "aprobado"
                      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                      : inc.status === "denegado"
                      ? "bg-red-100 text-red-700 border-red-300"
                      : "bg-yellow-100 text-yellow-700 border-yellow-300";
                  const kindBadge = isTarea
                    ? "bg-indigo-100 text-indigo-700 border-indigo-300"
                    : "bg-slate-100 text-slate-700 border-slate-300";
                  return (
                    <li
                      key={inc.id}
                      data-testid={`history-inc-${inc.id}`}
                      className="text-xs bg-white rounded-lg border border-[var(--border)] px-3 py-2"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-semibold">{inc.date}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wider font-display font-bold ${kindBadge}`}>
                          {isTarea ? "Tarea" : "Fichaje"}
                        </span>
                        {isTarea && inc.break_reason && (
                          <span className="text-[var(--text-secondary)]">· {inc.break_reason}</span>
                        )}
                        <span className="text-[var(--text-secondary)]">· {parts.join(" · ") || "—"}</span>
                        <span className={`ml-auto capitalize text-[10px] px-2 py-0.5 rounded-full border ${badge}`}>{inc.status}</span>
                      </div>
                      {inc.motivo && <div className="italic text-[var(--text-secondary)] mt-1">“{inc.motivo}”</div>}
                      {inc.status === "denegado" && inc.motivo_rechazo && (
                        <div className="mt-1 text-red-700" data-testid={`history-inc-rechazo-${inc.id}`}>
                          <span className="font-semibold">Rechazada:</span> “{inc.motivo_rechazo}”
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {data?.breaks && data.breaks.length > 0 && (
            <div className="px-5 py-3 border-b border-amber-200 bg-amber-50" data-testid="history-breaks">
              <div className="label-cap mb-2 text-amber-800">mis tareas</div>
              <ul className="space-y-1.5">
                {data.breaks.slice(0, 20).map((b) => (
                  <li key={b.id} data-testid={`history-break-${b.id}`} className="text-xs bg-white rounded-lg border border-amber-200 px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-amber-900">
                        {b.reason}{b.custom_reason ? ` — ${b.custom_reason}` : ""}
                      </span>
                      <span className="text-[var(--text-secondary)]">
                        · {new Date(b.start_time).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        {b.end_time && ` → ${new Date(b.end_time).toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit" })}`}
                      </span>
                      <span className="ml-auto font-mono font-semibold text-amber-800">{b.duration_hours.toFixed(2)} h</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-[var(--border)]">
                <th className="text-left p-3"><SortHeader sortKey="start_time" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Inicio</SortHeader></th>
                <th className="text-left p-3"><SortHeader sortKey="end_time" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Fin</SortHeader></th>
                <th className="text-right p-3"><SortHeader align="right" sortKey="duration_hours" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Duración</SortHeader></th>
                <th className="text-center p-3"><SortHeader align="center" sortKey="origin" currentKey={histSort.sortKey} currentDir={histSort.sortDir} onClick={histSort.toggleSort}>Estado</SortHeader></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="p-8 text-center text-[var(--text-secondary)]">Cargando…</td></tr>
              )}
              {!loading && (data?.punches?.length ?? 0) === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-[var(--text-secondary)]" data-testid="history-empty">Sin fichajes en este período.</td></tr>
              )}
              {!loading && histSort.sortedRows.map((p) => {
                const skipped = p.auto_closed || p.excluded;
                return (
                  <tr key={p.id} className={`border-b border-[var(--border)] last:border-0 ${skipped ? "opacity-60" : ""}`} data-testid={`history-row-${p.id}`}>
                    <td className={`p-3 font-mono ${skipped ? "line-through" : ""}`}>{fmt(p.start_time)}</td>
                    <td className={`p-3 font-mono ${skipped ? "line-through" : ""}`}>{p.is_open ? <span className="text-[var(--primary)] font-display font-bold">EN CURSO</span> : fmt(p.end_time)}</td>
                    <td className={`p-3 text-right font-mono ${skipped ? "line-through" : ""}`}>{p.duration_hours.toFixed(2)} h</td>
                    <td className="p-3 text-center">
                      {p.auto_closed && <span className="text-[10px] font-display font-bold uppercase tracking-wider bg-amber-500/15 text-amber-600 border border-amber-500/40 px-2 py-0.5 rounded">Auto-cierre</span>}
                      {p.excluded && !p.auto_closed && <span className="text-[10px] font-display font-bold uppercase tracking-wider bg-slate-500/15 text-slate-600 border border-slate-500/40 px-2 py-0.5 rounded">Excluido</span>}
                      {p.origin === "incidence" && <span className="text-[10px] font-display font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-600 border border-emerald-500/40 px-2 py-0.5 rounded">Incidencia</span>}
                      {!p.auto_closed && !p.excluded && p.origin !== "incidence" && <span className="text-[10px] text-[var(--text-secondary)]">Normal</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-[var(--border)] bg-[var(--bg)] flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-[var(--text-secondary)] flex-1 min-w-[180px]">
            Vista de solo lectura. Si necesitas corregir un fichaje, abre una incidencia.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={printList}
              disabled={loading || !(data?.punches?.length)}
              data-testid="history-print-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[var(--border)] bg-white font-display font-semibold text-sm hover:bg-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Printer size={16} weight="duotone" />
              Imprimir listado
            </button>
            {onOpenIncidence && (
              <button
                onClick={() => onOpenIncidence(worker)}
                data-testid="history-open-incidence-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl btn-primary font-display font-bold text-sm"
              >
                <Warning size={16} weight="duotone" />
                Abrir incidencia
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
